<template lang="pug">
    button(
        ref="_in",
        type="button",
        class="_btn",
        :class="{ [tone]: tone, [appearance]: appearance, _busy: busy }",
        :style="{ width, height }",
        :id="id",
        :disabled="disabled || busy",
        :inert="inert",
        :tabindex="inert ? -1 : tabindex",
        v-on="__on")
        slot &#xfeff;
        span(v-if="busy", class="_busyi")
            slot(name="busy")
                ib-progress-dots
</template>

<script>
    import {
        bindKey,
        unbindKey
    } from '../../js/window'

    import entry from '../form/ib-form-entry.vue';
    import telemetry from '../private/telemetry';

    function noop() { }


    /**
     * A stylized <button> control with minor behavior enhancements.
     */
    export default {
        name: 'ib-button',

        mixins: [
            entry,
            telemetry
        ],

        props: {
            /**
             * Semantic color class.
             */
            tone: String,
            /**
             * Appearance modifier: `outline`, `clear`, `link`, `pill`, `fab`, and `flat` are supported.
             */
            appearance: String,
            /**
             * If true, display the busy placeholder and act as disabled.
             */
            busy: Boolean,
            /**
             * If true, take focus when loaded or made visible.
             */
            autofocus: Boolean,
            /**
             * Key or key combination to bind to this element.
             */
            bind: String
        },

        data() {
            return {
                bound: null
            };
        },

        watch: {
            bind(bind) {
                this.bindKey(bind);
            }
        },

        mounted() {
            this.bindKey(this.bind);
            if (this.$device.ios) {
                this.$el.addEventListener('touchstart', noop, true);
            }
        },

        destroyed() {
            this.bindKey(null);
        },

        methods: {
            /** Can be called externally to simulate a click */
            click() {
                this.$emit('click');
            },

            bindKey(bind) {
                if (this.bound) {
                    unbindKey(this.bound, this.$el);
                }
                if (bind) {
                    bindKey(bind, this.$el);
                    this.bound = bind;
                }
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    .btn-solid(@fill, @stroke) {
        color: @stroke;
        background-color: @fill;
    }
    .btn-outline(@fill, @stroke) {
        color: @stroke;
        border-color: @fill;
    }

    @keyframes _btn-busy {
        0% {
            opacity: 0;
        } 100% {
            opacity: 1;
        }
    }

    ._btn {
        position: relative;
        margin: 2px 4px;
        padding: 0 @btn-padding;
        border: 0 solid;
        border-radius: @btn-radius;
        color: @fg-button;
        background-color: @bg-button;
        font-family: @font-family;
        .font-size(@fs6);
        text-align: center;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: calc(~'100% - 8px');
        cursor: pointer;
        z-index: 0;
        -webkit-tap-highlight-color: transparent;

        /* ::before pseudo-element: outline border and disabled overlay */
        /* ::after pseudo-element: hover overlay */

        &::before,
        &::after {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            border-radius: inherit;
            pointer-events: none;
        }
        &::before {
            border: inherit;
        }
        &::after {
            opacity: 0;
        }

        &:focus {
            outline: none;
        }
        &::-moz-focus-inner {
            padding: 0;
            border: 0;
        }
        &:invalid {
            outline: none;
        }
        &.@{btn-focus},
        &.@{btn-hover},
        &:@{btn-focus},
        &:@{btn-hover} {
            &::after {
                opacity: .08;
            }
        }
        &:disabled {
            cursor: default;
            &::before {
                content: '' !important;
                z-index: 2;
                background-color: @bg;
                opacity: .62;
            }
            &.link {
                opacity: .28;
            }
        }
        .btn-default();
    }

    ._btn.flat,
    ._tbec ._btn,
    ._tbsc ._btn {
        .btn-flat();
    }

    ._btn.outline {
        .btn-outline();
    }

    ._btn {
        & {
            .height(@btn-height + 2);
        }
        &.xs {
            .font-size(@fs8);
            .height(@btn-height-xs + 2);
        }
        &.sm {
            .font-size(@fs7);
            .height(@btn-height-sm + 2);
        }
        &.lg {
            .height(@btn-height-lg + 2);
        }
        &.xl {
            .height(@btn-height-xl + 2);
        }

        &.accent    { .btn-solid(@bg-accent, #fff); }
        &.primary   { .btn-solid(@bg-accent, #fff); }
        &.positive  { .btn-solid(@bg-positive, #fff); }
        &.negative  { .btn-solid(@bg-negative, #fff); }
        &.buy       { .btn-solid(@bg-buy, #fff); }
        &.hold      { .btn-solid(@bg-hold, #fff); }
        &.sell      { .btn-solid(@bg-sell, #fff); }
        &.ok        { .btn-solid(@bg-ok, #fff); }
        &.warning   { .btn-solid(@bg-warning, #000); }
        &.error     { .btn-solid(@bg-error, #fff); }
        &.gray      { .btn-solid(@bg-gray, @fg-button); }
        &.outline,
        &.clear     {
            color: @fg-gray;
        }
        &.link      {
            color: @fg-accent;
        }
        &.outline,
        &.clear,
        &.link {
            &.accent,
            &.primary   { .btn-outline(@bg-accent, @fg-accent); }
            &.positive  { .btn-outline(@bg-positive, @fg-positive); }
            &.negative  { .btn-outline(@bg-negative, @fg-negative); }
            &.buy       { .btn-outline(@bg-buy, @fg-buy); }
            &.hold      { .btn-outline(@bg-hold, @fg-hold); }
            &.sell      { .btn-outline(@bg-sell, @fg-sell); }
            &.ok        { .btn-outline(@bg-ok, @fg-ok); }
            &.warning   { .btn-outline(@bg-warning, @fg-warning); }
            &.error     { .btn-outline(@bg-error, @fg-error); }
            &.gray      { .btn-outline(@bg-gray, @fg-gray); }
        }

        /** No background */
        &.outline {
            background-color: @bg;
            & when (iscolor(@fg-button-outline)) {
                color: @fg-button-outline;
            }
            & when (iscolor(@border-button-outline)) {
                border-color: @border-button-outline;
            }
        }
        &.clear,
        &.link {
            background-color: transparent;
            &::before {
                border: none;
            }
        }

        /** No button profile or background; useful for links/icons */
        &.link {
            max-width: none;
            height: auto;
            line-height: inherit;
            margin: 0;
            padding: 0;
            will-change: filter;

            &.@{btn-focus},
            &.@{btn-hover},
            &:@{btn-focus},
            &:@{btn-hover} {
                &:not(:disabled) {
                    outline: 0;
                    .a-hover();
                }
            }
            &::before,
            &::after {
                content: none !important;
            }
        }

        /** Reduced left and right padding */
        &.narrow {
            padding-left: 8px;
            padding-right: 8px;
        }

        /** Pill button - rounded ends */
        &.pill {
            border-radius: 999px;
        }

        /** Floating action button - round */
        &.fab {
            width: 2.25em;
            height: 2.25em;
            max-width: none;
            margin: 0;
            padding: 0;
            border-radius: 50%;
            line-height: 0;
            &.xs {
                width: 1.75em;
                height: 1.75em;
            }
            &.sm {
                width: 2em;
                height: 2em;
            }
            &.lg {
                width: 2.5em;
                height: 2.5em;
            }
            &.xl {
                width: 2.75em;
                height: 2.75em;
            }
        }

        /** Busy state */
        &._busy {
            &::before {
                opacity: .31;
            }
            &::after {
                z-index: 0;
            }
        }
        > ._busyi {
            position: absolute;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: inherit;
            border-radius: inherit;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            animation: _btn-busy .4s;

            > ._dots {
                width: 50%;
                height: 50%;
                min-width: 2em;
                max-width: 3em;
            }
        }

        /** Badge position */
        > ._badge {
            top: -7px;
            right: 4px;
            z-index: 3;
        }
    }

    /** Traditional Chinese positive/negative colors */

    ._trcc ._btn {
        &.positive  { .btn-solid(@bg-positive-trcc, #fff); }
        &.negative  { .btn-solid(@bg-negative-trcc, #fff); }
        &.outline,
        &.clear,
        &.link {
            &.positive  { .btn-outline(@bg-positive-trcc, @fg-positive-trcc); }
            &.negative  { .btn-outline(@bg-negative-trcc, @fg-negative-trcc); }
        }
        &.outline {
            background-color: @bg;
        }
        &.clear,
        &.link {
            background-color: transparent;
        }
    }
</style>

<docs>
    Basic Example:

    ```jsx
        <ib-context>
            <p>Basic buttons:</p>
            <p>
                <ib-button v-for="tone in [null,'accent','positive','negative','buy','sell','gray','warning']" :tone="tone">{{tone || 'Default'}}</ib-button>
            </p>
            <p class="before-lg">Outline buttons:</p>
            <p>
                <ib-button v-for="tone in [null,'accent','positive','negative','buy','sell','gray','warning']" appearance="outline" :tone="tone">{{tone || 'Default'}}</ib-button>
            </p>
            <p class="before-lg">Sizes (note, text baseline is always even):</p>
            <p>
                <ib-button v-for="cls in ['xs','sm',null,'lg','xl']" :class="cls">{{cls ? '.'+cls : 'Default'}}</ib-button>
            </p>
            <p class="before-lg">Icon Examples:</p>
            <p>
                <ib-button><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M19.28 5.28L9 15.56l-4.28-4.28-1.44 1.44 5 5 .72.69.72-.7 11-11z"/></svg></ib-button>
                <ib-button class="link"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M19.28 5.28L9 15.56l-4.28-4.28-1.44 1.44 5 5 .72.69.72-.7 11-11z"/></svg></ib-button>
                <ib-button><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M19.28 5.28L9 15.56l-4.28-4.28-1.44 1.44 5 5 .72.69.72-.7 11-11z"/></svg> Confirm</ib-button>
            </p>
            <p class="before-lg">Other classes:</p>
            <p>
                <ib-button v-for="cls in ['link','clear','narrow','pill']" :class="cls">.{{cls}}</ib-button>
                &nbsp;&nbsp;&nbsp;.fab: <ib-button class="fab"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M19.28 5.28L9 15.56l-4.28-4.28-1.44 1.44 5 5 .72.69.72-.7 11-11z"/></svg></ib-button>
            </p>
            <p class="before-lg">Attributes:</p>
            <p>
                <ib-button disabled="disabled">:disabled</ib-button>
                <ib-button inert="inert">:inert</ib-button>
            </p>
        </ib-context>
    ```
</docs>
